import { Type } from "@sinclair/typebox";
import { Mimir } from "./mimir";
import { NrkDataSchema, digitalSourceTypeGroupLabels, isPhotographer, } from "./nrk-data";
const MetadataSchema = Type.Partial(Type.Composite([
    Type.Omit(Mimir.Form.AllFieldsSchema, ["data"]),
    NrkDataSchema,
    Type.Object({
        detectedLabels: Mimir.DetectedLabelsSchema,
        simplifiedDigitalSourceType: Type.Union(digitalSourceTypeGroupLabels.map((label) => Type.Literal(label))),
    }),
]));
export const SUPPORTED_MIME_TYPES = [
    "image/*",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/heic",
    // "image/tiff", // not supported by Kaleido
    // "image/x-sony-arw", // Sony ARW files
    // "image/x-canon-cr2", // Canon CR2 files
    // "image/x-nikon-nef", // Nikon NEF files
    //"image/dng",
    "image/avif",
];
//Fields only accessed & used by Bildebanken
export const LocalFields = Type.Object({
    altTextSuggestion: Type.Optional(Type.String()),
});
export const ImageSchema = Type.Composite([
    Type.Object({
        metadata: MetadataSchema,
    }),
    Type.Object({
        technicalMetadata: Type.Optional(Mimir.TechnicalMetadataSchema),
    }),
    Type.Omit(Mimir.FullItemMetadataSchema, ["metadata", "technicalMetadata"]),
    LocalFields,
], {
    $id: "Image",
    description: "Metadata for a Bildebanken Image",
    title: "Image",
    altTextSuggestion: "AI suggested alt text",
});
export const FolderMetadataSchema = Type.Object({
    displayName: Type.Optional(Type.String()),
});
export const CollectionSchema = Type.Object({
    id: Type.String(),
    name: Type.String(),
    owner: Type.String(),
    visibleTo: Mimir.AssetVisibilitySchema,
    metadata: FolderMetadataSchema,
});
export const CollectionImageSchema = Type.Object({
    id: Type.String(),
    thumbnail: Type.Optional(Type.String()),
});
export const DerivateResponseSchema = Type.Object({
    id: Type.String(),
    x: Type.Number(),
    y: Type.Number(),
    w: Type.Number(),
    h: Type.Number(),
    width: Type.Integer(),
    height: Type.Integer(),
    quality: Type.Number(),
    format: Type.String(),
    type: Type.String(),
    orientation: Type.Integer(),
    transformation: Type.Array(Type.String()),
    uri: Type.String(),
    location: Type.String({ format: "url" }),
});
export const usageDomains = [
    {
        name: "Redaksjonelt innhold",
        value: "http://authority.nrk.no/datadictionary/editorialImage",
    },
    {
        name: "Promo",
        value: "http://authority.nrk.no/datadictionary/promotionalImage",
    },
    {
        name: "Internt",
        value: "http://authority.nrk.no/datadictionary/internalImage",
    },
];
export const EditableMetadataSchema = Type.Composite([
    Type.Partial(Type.Omit(Mimir.Form.EditableFieldsSchema, [
        "data",
        "creators",
        "personInImage",
        "copyrightNotice",
        "usageTerms",
        "creditLine",
        "copyrightOwners",
        "publicAvailabilityStatus",
        "publicId",
    ])),
    Type.Partial(NrkDataSchema),
], { description: "Metadata fields editable via Bildebanken API", additionalProperties: false });
export const EditableLocalFieldsSchema = Type.Composite([Type.Partial(LocalFields)], {
    description: "Fields only relevant to Bildebanken UI",
});
export const ImagePatchSchema = Type.Object({
    metadataVersion: Type.Optional(Type.Number({ description: "If specified, version checking will be performed" })),
    metadataDelta: Type.Optional(EditableMetadataSchema),
    visibleTo: Type.Optional(Mimir.AssetVisibilitySchema),
    ownerEmail: Type.Optional(Type.String({
        format: "email",
        description: "NRK user email, case sensitive (typically title case)",
    })),
}, { additionalProperties: false });
export function itemIsImage(item) {
    return !("formData" in item.metadata);
}
/**
 * For situations where you can either have a SearchResultItem or an Image
 * and want to extract some data from the item, this function maps the data to an
 * object that contains the common fields. The return type is marked as Partial to
 * approximate the differences between the two types.
 *
 * This a simplistic utility function which might need to be updated to a more
 * sophisticated implementation if the two types diverge more. Typescript may
 * or may not be able to detect when this happens.
 */
export function toPartialImage(item) {
    if (itemIsImage(item)) {
        return Object.assign({}, item);
    }
    else {
        return Object.assign(Object.assign({}, item), { metadata: item.metadata.formData });
    }
}
/**
 * If the provided image has a Rights object the rights marker is calculated from that.
 *
 * Otherwise, an attempt is made to calculate the rights marker from the available data.
 *
 * The rule is that if an image belongs to NRK and has no usage terms, the rights marker is "Free",
 * If there is a copyright owner (or notice) and/or usage terms (rights.note), it is "Restricted".
 * If none of the above is available, the marker is "Unknown".
 */
export function getRightsMarker(imageOrSearchHit) {
    if (!imageOrSearchHit ||
        typeof imageOrSearchHit !== "object" ||
        !("metadata" in imageOrSearchHit)) {
        return "Unknown";
    }
    const metadata = "formData" in imageOrSearchHit.metadata
        ? imageOrSearchHit.metadata.formData
        : imageOrSearchHit.metadata;
    // Handle Image first because we want to ignore the rightsMarker and calculate from scratch if possible
    if ("rights" in metadata) {
        return calculateRightsMarker(metadata.rights, metadata.instructions);
    }
    // For SearchHit
    if ("rightsMarker" in metadata)
        return metadata.rightsMarker;
    // fallback logic, e.g. for imported images with only IPTC fields
    const copyrightNotice = metadata.copyrightNotice;
    const usageTerms = metadata.usageTerms;
    if (!copyrightNotice && !usageTerms)
        return "Unknown";
    return copyrightNotice === "NRK" && !usageTerms ? "Free" : "Restricted";
}
export function calculateRightsMarker(rights, instructions) {
    var _a, _b;
    if (!rights)
        return "Unknown";
    if (rights.note)
        return "Restricted";
    if (instructions)
        return "Restricted";
    if (!((_b = (_a = rights.rightsHolder) === null || _a === void 0 ? void 0 : _a.contact) === null || _b === void 0 ? void 0 : _b.title))
        return "Unknown";
    return "Free";
}
export function getPhotographer(image) {
    var _a;
    return (_a = image.metadata.contributors) === null || _a === void 0 ? void 0 : _a.find(isPhotographer);
}
export function calculateCreditLine(metadata) {
    var _a, _b, _c, _d, _e, _f;
    if (!metadata)
        return undefined;
    if ((_a = metadata.rights) === null || _a === void 0 ? void 0 : _a.credit)
        return (_b = metadata.rights) === null || _b === void 0 ? void 0 : _b.credit;
    let creditLine = "";
    const title = (_e = (_d = (_c = metadata.rights) === null || _c === void 0 ? void 0 : _c.rightsHolder) === null || _d === void 0 ? void 0 : _d.contact) === null || _e === void 0 ? void 0 : _e.title;
    const creators = ((_f = metadata.contributors) === null || _f === void 0 ? void 0 : _f.filter(isPhotographer).map((ph) => { var _a; return (_a = ph.contact) === null || _a === void 0 ? void 0 : _a.title; }).filter(Boolean)) || metadata.creators;
    if ((creators === null || creators === void 0 ? void 0 : creators.length) > 0)
        creditLine += creators.join(", ");
    if (title && (creators === null || creators === void 0 ? void 0 : creators.length) > 0)
        creditLine += " / ";
    if (title)
        creditLine += title;
    if (creators && creators.join("") === title)
        return creators.join("");
    return creditLine === "" ? undefined : creditLine;
}
//# sourceMappingURL=bildebanken.js.map